/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import CampaignModel from 'cio/models/campaign';
import chunk from 'lodash.chunk';
import { capitalize } from '@ember/string';
import AccountService from 'cio/services/account';
import VisualWorkflowBuilderService from 'cio/services/visual-workflow-builder';
import AnalyticsService from 'cio/services/analytics';
import moment from 'moment-timezone';
import {
  LAST_30_DAYS_RANGE_PRESET,
  LAST_12_WEEKS_RANGE_PRESET,
  LAST_12_MONTHS_RANGE_PRESET,
  AVAILABLE_WORKFLOW_MEASURE_OPTIONS,
  LAST_7_DAYS_RANGE_PRESET,
} from 'cio/utils/workflow-metrics';
import { RangePreset } from 'cio/utils/date-picker';
import { Periods } from 'cio/utils/metrics/common';
import { TimeRange } from 'cio/utils/time';

interface VisualWorkflowBuilderAddActionsPanelMetricsArgs {
  campaign: CampaignModel;
  metricsTabSelectedMeasures: string[];
  selectMetricsTabMeasure: (measure: string) => unknown;
  deselectMetricsTabMeasure: (measure: string) => unknown;
  metricsTabTimePeriod: Periods;
  metricsTabTimeRange: TimeRange;
  changeMetricsTabTimePeriod: (period: Periods) => unknown;
  changeMetricsTabTimeRange: (range: TimeRange) => unknown;
}

export default class VisualWorkflowBuilderAddActionsPanelMetrics extends Component<VisualWorkflowBuilderAddActionsPanelMetricsArgs> {
  @service account!: AccountService;
  @service analytics!: AnalyticsService;
  @service visualWorkflowBuilder!: VisualWorkflowBuilderService;

  isShowingMetricsFeedbackForm = false;

  defaultPreset = LAST_30_DAYS_RANGE_PRESET;

  _timeRange: any = null;

  _availableMetricItems = AVAILABLE_WORKFLOW_MEASURE_OPTIONS;

  get _groupedAvailableMetricItems() {
    return chunk(this._availableMetricItems, 2);
  }

  get dateRangePresets(): RangePreset[] {
    return [
      LAST_7_DAYS_RANGE_PRESET,
      LAST_30_DAYS_RANGE_PRESET,
      LAST_12_WEEKS_RANGE_PRESET,
      LAST_12_MONTHS_RANGE_PRESET,
    ];
  }

  get _canSelectMetrics() {
    return this.args.metricsTabSelectedMeasures.length < 4;
  }

  get timeRange() {
    if (this.args.metricsTabTimeRange != null) {
      return this.args.metricsTabTimeRange;
    }

    let preset = this.dateRangePresets.find((preset) => preset.value === this.args.metricsTabTimePeriod);

    if (preset?.getRange == null) {
      return null;
    }

    return preset.getRange();
  }

  @action
  addMetricItem(item: any) {
    this.args.selectMetricsTabMeasure(item);
    this.visualWorkflowBuilder.redrawConnectingLines();

    this.analytics.track('campaign', 'workflow_metrics_tab_chosen_metrics_changed', {
      model: this.args.campaign,
      timePeriod: this.args.metricsTabTimePeriod,
      timeRange: this.args.metricsTabTimeRange,
      selectedMetrics: this.args.metricsTabSelectedMeasures.map((metric) => capitalize(metric)),
    });
  }

  @action
  removeMetricItem(item: any) {
    this.args.deselectMetricsTabMeasure(item);
    this.visualWorkflowBuilder.redrawConnectingLines();

    this.analytics.track('campaign', 'workflow_metrics_tab_chosen_metrics_changed', {
      model: this.args.campaign,
      timePeriod: this.args.metricsTabTimePeriod,
      timeRange: this.args.metricsTabTimeRange,
      selectedMetrics: this.args.metricsTabSelectedMeasures.map((metric) => capitalize(metric)),
    });
  }

  @action
  onRangeSelect(timeRange: { start: moment.Moment; end: moment.Moment }, timePeriod: Periods) {
    this.args.changeMetricsTabTimeRange(timeRange);
    this.args.changeMetricsTabTimePeriod(timePeriod);

    this.analytics.track('campaign', 'workflow_metrics_tab_time_period_changed', {
      model: this.args.campaign,
      timePeriod: this.args.metricsTabTimePeriod,
      timeRange: this.args.metricsTabTimeRange,
      selectedMetrics: this.args.metricsTabSelectedMeasures.map((metric) => capitalize(metric)),
    });
  }
}
